/**
 * The route tree.
 *
 * Code-based rather than file-based. TanStack's file-based routing generates a
 * `routeTree.gen.ts` that has to be committed and kept in step with the
 * filesystem by a plugin, and this application has under twenty routes. A generated
 * artefact and a build plugin to maintain eight routes is the kind of thing
 * ADR-0003's "do not over-engineer" is about; when the tree is forty routes
 * deep, switching is a mechanical change.
 *
 * # Authentication is a render decision, not a redirect
 *
 * The root component reads the session and renders one of four things: the
 * loading state, the sign-in page, the MFA challenge, or the shell. There is no
 * `beforeLoad` redirect to `/sign-in` and no `?redirect=` round trip.
 *
 * Three reasons. A redirect means the user's URL is destroyed and has to be
 * restored afterwards, which is the bug every application with a `?redirect=`
 * parameter has. A redirect flashes: the guard runs after the route has begun
 * rendering, so a protected page paints for a frame before it is replaced. And
 * the MFA state has no URL of its own that makes sense — it is not a page the
 * user can navigate to or away from, it is a gate the server is holding closed.
 *
 * Rendering the gate in place keeps the address bar pointing at where the user
 * was going, so finishing the challenge lands them there with no bookkeeping.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  type AnyRoute,
} from '@tanstack/react-router';

import { useSessionContext } from '~/auth/session';
import { AppShell } from '~/shell/AppShell';
import { Alert, Button, Icon, SkeletonRows } from '~/components';

import styles from './router.module.css';
import { NotFound } from '~/routes/NotFound';
import { MFAChallenge, SignIn } from '~/routes/SignIn';

/**
 * Which of the seven session phases the visitor is in decides what renders.
 *
 * An exhaustive `switch`, so a phase added later is a compile error here
 * rather than a blank screen. There is no `default` for the same reason.
 */
function RootComponent() {
  const { phase, error, refetch } = useSessionContext();

  switch (phase) {
    case 'unknown':
      // Not a spinner: the shell's own shape, so the page does not jump when
      // the session resolves. §1 — "loading states are designed".
      return (
        <div className={styles.boot}>
          <SkeletonRows count={5} label="Loading Exi Git" />
        </div>
      );

    case 'network_error':
      return (
        <SessionProblem
          title="Cannot reach Exi Git"
          detail="The application could not reach the API, so it cannot tell whether you are signed in. This may be your connection."
          traceId={error?.traceId}
          onRetry={refetch}
        />
      );

    case 'auth_error':
      // The API answered, and not usefully. Distinct from the above because
      // there is nothing the user can do about this one.
      return (
        <SessionProblem
          title="Exi Git is not responding"
          detail="The API answered with an error, so the application cannot tell whether you are signed in."
          traceId={error?.traceId}
          onRetry={refetch}
        />
      );

    case 'mfa_required':
      return <MFAChallenge />;

    case 'session_expired':
      // The sign-in form, but saying why they are looking at it. Somebody who
      // was working a second ago should not have to guess.
      return <SignIn expired />;

    case 'unauthenticated':
      return <SignIn />;

    case 'authenticated':
      return (
        <AppShell>
          <Outlet />
        </AppShell>
      );
  }
}

function SessionProblem({
  title,
  detail,
  traceId,
  onRetry,
}: {
  title: string;
  detail: string;
  traceId: string | undefined;
  onRetry: () => void;
}) {
  return (
    <div className={styles.bootPanel}>
      <Alert
        tone="danger"
        live
        title={title}
        actions={
          <Button iconStart={<Icon.Activity />} onClick={onRetry}>
            Try again
          </Button>
        }
      >
        <p>{detail}</p>
        {traceId ? (
          <p className={styles.bootTrace}>
            Trace <code>{traceId}</code>
          </p>
        ) : null}
      </Alert>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFound,
});

/**
 * One route, loaded on demand.
 *
 * ADR-0003 lists "small, tree-shaken bundles; code-split by route" as a
 * consequence of choosing Vite, and a single chunk containing every page is
 * that promise unkept: somebody opening the sign-in screen downloads the
 * settings page, the inbox and the security policy view in order to type a
 * password.
 *
 * `SignIn`, `MFAChallenge`, `NotFound` and the shell stay eager, because they
 * are what the first paint needs in every case and lazy-loading them would
 * replace one network round trip with two.
 */
function page(
  path: string,
  load: () => Promise<Record<string, unknown>>,
  exportName: string,
): AnyRoute {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: lazyRouteComponent(load as never, exportName as never),
  });
}

const routes = [
  page('/', () => import('~/routes/Overview'), 'Overview'),
  page('/repositories', () => import('~/routes/Repositories'), 'Repositories'),
  page('/activity', () => import('~/routes/Activity'), 'Activity'),
  page('/inbox', () => import('~/routes/Inbox'), 'Inbox'),
  page('/security', () => import('~/routes/SecurityFindings'), 'SecurityFindings'),
  page('/security/policy', () => import('~/routes/Security'), 'Security'),
  page('/settings', () => import('~/routes/Settings'), 'Settings'),
  page('/organization', () => import('~/routes/Organization'), 'Organization'),
  page('/organization/review-rules', () => import('~/routes/ReviewRules'), 'ReviewRules'),
  page('/projects', () => import('~/routes/Projects'), 'Projects'),
  page('/projects/$projectId', () => import('~/routes/Projects'), 'ProjectBoard'),
  page('/integrations', () => import('~/routes/Integrations'), 'Integrations'),
  page('/integrations/$integrationId', () => import('~/routes/Integrations'), 'IntegrationDetail'),
  page('/governance', () => import('~/routes/Governance'), 'Governance'),
  page('/governance/policies/$policyId', () => import('~/routes/Governance'), 'PolicyDetailPage'),
  page('/automations', () => import('~/routes/Automations'), 'Automations'),
  page('/automations/new', () => import('~/routes/AutomationBuilder'), 'AutomationBuilder'),
  page('/automations/$automationId', () => import('~/routes/Automations'), 'AutomationDetail'),
  page(
    '/automations/$automationId/edit',
    () => import('~/routes/AutomationBuilder'),
    'AutomationBuilder',
  ),
  page(
    '/automations/$automationId/executions/$executionId',
    () => import('~/routes/Automations'),
    'AutomationExecutionPage',
  ),
  page('/search', () => import('~/routes/Search'), 'Search'),
  page('/audit', () => import('~/routes/AuditLog'), 'AuditLog'),

  // Observability (DL-245): the organization's services, their SLOs, a
  // metrics explorer over its telemetry sources, and incidents across the
  // repositories the reader can see.
  page('/observability', () => import('~/routes/observability/Overview'), 'ObservabilityOverview'),
  page('/observability/services', () => import('~/routes/observability/Services'), 'Services'),
  page(
    '/observability/services/$serviceId',
    () => import('~/routes/observability/ServiceDetail'),
    'ServiceDetail',
  ),
  page(
    '/observability/services/$serviceId/slos/$sloId',
    () => import('~/routes/observability/SloDetail'),
    'SloDetail',
  ),
  page('/observability/slos', () => import('~/routes/observability/Slos'), 'Slos'),
  page('/observability/metrics', () => import('~/routes/observability/Metrics'), 'Metrics'),
  page(
    '/observability/incidents',
    () => import('~/routes/observability/Incidents'),
    'OrganizationIncidents',
  ),

  // Traceability (DL-245): the delivery record read as lineage.
  page('/traceability', () => import('~/routes/traceability/Traceability'), 'Traceability'),
  page(
    '/traceability/$repoId/commits/$sha',
    () => import('~/routes/traceability/CommitLineage'),
    'CommitLineage',
  ),
  page(
    '/traceability/$repoId/deployments/$deploymentId',
    () => import('~/routes/traceability/DeploymentLineage'),
    'DeploymentLineage',
  ),
  page(
    '/traceability/$repoId/artifacts',
    () => import('~/routes/traceability/History'),
    'ArtifactHistory',
  ),
  page(
    '/traceability/$repoId/releases',
    () => import('~/routes/traceability/History'),
    'ReleaseHistory',
  ),
  page(
    '/traceability/$repoId/environments',
    () => import('~/routes/traceability/History'),
    'EnvironmentView',
  ),

  // A repository and everything inside it. The organization is part of the
  // path so a link works whichever organization the reader has selected.
  page('/repositories/$orgId/$repoId', () => import('~/routes/repo/Code'), 'RepositoryCode'),
  page(
    '/repositories/$orgId/$repoId/commits',
    () => import('~/routes/repo/History'),
    'RepositoryCommits',
  ),
  page(
    '/repositories/$orgId/$repoId/commits/$sha',
    () => import('~/routes/repo/History'),
    'RepositoryCommit',
  ),
  page(
    '/repositories/$orgId/$repoId/branches',
    () => import('~/routes/repo/History'),
    'RepositoryBranches',
  ),
  page(
    '/repositories/$orgId/$repoId/pulls',
    () => import('~/routes/repo/Pulls'),
    'RepositoryPulls',
  ),
  page(
    '/repositories/$orgId/$repoId/pulls/new',
    () => import('~/routes/repo/Pulls'),
    'NewPullRequest',
  ),
  page(
    '/repositories/$orgId/$repoId/pulls/$number',
    () => import('~/routes/repo/Pulls'),
    'PullRequestPage',
  ),
  page(
    '/repositories/$orgId/$repoId/issues',
    () => import('~/routes/repo/Issues'),
    'RepositoryIssues',
  ),
  page('/repositories/$orgId/$repoId/issues/new', () => import('~/routes/repo/Issues'), 'NewIssue'),
  page(
    '/repositories/$orgId/$repoId/issues/$number',
    () => import('~/routes/repo/Issues'),
    'IssuePage',
  ),
  page(
    '/repositories/$orgId/$repoId/actions',
    () => import('~/routes/repo/Delivery'),
    'RepositoryActions',
  ),
  page(
    '/repositories/$orgId/$repoId/actions/$runId',
    () => import('~/routes/repo/FailureAnalysis'),
    'WorkflowRunPage',
  ),
  page(
    '/repositories/$orgId/$repoId/releases',
    () => import('~/routes/repo/Delivery'),
    'RepositoryReleases',
  ),
  page(
    '/repositories/$orgId/$repoId/deployments',
    () => import('~/routes/repo/Delivery'),
    'RepositoryDeployments',
  ),
  page(
    '/repositories/$orgId/$repoId/deployments/$deploymentId',
    () => import('~/routes/repo/FailureAnalysis'),
    'DeploymentPage',
  ),
  page(
    '/repositories/$orgId/$repoId/insights',
    () => import('~/routes/repo/Delivery'),
    'RepositoryInsights',
  ),
  page(
    '/repositories/$orgId/$repoId/security',
    () => import('~/routes/repo/Reliability'),
    'RepositorySecurity',
  ),
  page(
    '/repositories/$orgId/$repoId/security/findings/$findingId',
    () => import('~/routes/repo/SecurityFinding'),
    'RepositorySecurityFinding',
  ),
  page(
    '/repositories/$orgId/$repoId/incidents',
    () => import('~/routes/repo/Reliability'),
    'RepositoryIncidents',
  ),
  page(
    '/repositories/$orgId/$repoId/incidents/$number',
    () => import('~/routes/repo/IncidentDetail'),
    'IncidentDetail',
  ),
  page(
    '/repositories/$orgId/$repoId/traceability',
    () => import('~/routes/repo/Traceability'),
    'RepositoryTraceability',
  ),
  page(
    '/repositories/$orgId/$repoId/knowledge',
    () => import('~/routes/repo/Knowledge'),
    'RepositoryKnowledge',
  ),
  page(
    '/repositories/$orgId/$repoId/settings',
    () => import('~/routes/repo/RepoSettings'),
    'RepositorySettings',
  ),
];

const routeTree = rootRoute.addChildren(routes);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFound,
  // What shows while a route chunk is in flight. Shaped like the content
  // rather than being a spinner, and delayed by 150ms so a chunk that is
  // already cached never flashes a placeholder (DESIGN_LANGUAGE.md §7.1).
  defaultPendingMs: 150,
  defaultPendingComponent: () => (
    <div className={styles.routePending}>
      <SkeletonRows count={6} label="Loading this page" />
    </div>
  ),
  // Scroll to the top on navigation, but keep the position when the user comes
  // back — which is what a list of fifty audit entries needs and what the
  // browser does for a multi-page site for free.
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
